import { useState, useRef, useEffect } from "react";
import { useChat } from "../contexts/ChatContext";
import { ChatSidebar } from "./ChatSidebar";
import { ProfileMenu } from "./ProfileMenu";
import { AudioPlayer } from "./AudioPlayer";

export function Chat() {
  const [input, setInput] = useState("");
  const { messages, sendMessage, isLoading } = useChat();
  const messagesEndRef = useRef(null);
  const [isShining, setIsShining] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    await sendMessage(input);
    setInput("");
  };

  // Helper function to determine if a message is still being generated
  const isMessageGenerating = (message, index) => {
    return isLoading &&
      index === messages.length - 1 &&
      message.role === "assistant" &&
      !message.isGeneratingScript &&
      !message.content.includes("WTF with Aisha Comedy Script:");
  };

  // Helper function to determine if audio should be generating
  const isAudioGenerating = (message) => {
    return message.role === "assistant" &&
      message.content.includes("WTF with Aisha Comedy Script:") &&
      !message.additional_kwargs?.audio_data;
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <div className="gradient-bg">
        <ChatSidebar />
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat header */}
        <div className="relative">
          <div className="glass-header gradient-bg">
            <div
              className={`shine-effect ${isShining ? 'animate-shine-hover' : 'animate-shine-once'}`}
              onAnimationEnd={() => setIsShining(false)}
            />
            <div className="relative w-full px-2 py-4">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center space-x-4">
                  <div
                    className="glass-morphism-dark bg-emerald-800/30 px-6 py-3 rounded-full flex items-center space-x-3 border border-emerald-600/20 hover:bg-emerald-800/40 transition-all duration-300"
                    onMouseEnter={() => setIsShining(true)}
                  >
                    <h1 className="text-2xl font-bold text-emerald-300/90 cursor-pointer">
                      WFT with Aisha
                    </h1>
                    <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
                  </div>
                  <div className="glass-morphism px-4 py-2 rounded-full bg-emerald-700/30">
                    <p className="text-sm bg-gradient-to-r from-emerald-500 to-emerald-300 bg-clip-text text-transparent">Your personalized late night comedy show</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute right-4 top-4">
            <ProfileMenu />
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-emerald-950/30">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
            >
              <div
                className={`max-w-[80%] p-4 rounded-xl shadow-lg ${message.role === "user"
                    ? "glass-morphism ml-4"
                    : "glass-morphism-dark mr-4"
                  } ${isMessageGenerating(message, index) ? "animate-pulse" : ""
                  }`}
              >
                <div className="whitespace-pre-wrap break-words text-emerald-50/90">
                  {message.content}
                </div>

                {/* Show generating states or audio player */}
                {message.role === "assistant" && (
                  <>
                    {message.isGeneratingScript && (
                      <div className="flex flex-col items-start space-y-2 mt-2">
                        <div className="flex items-center space-x-2">
                          <div className="glass-morphism flex items-center justify-center w-10 h-10 rounded-full">
                            <div className="h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                          <span className="text-sm text-gray-300">Generating comedy script...</span>
                        </div>
                      </div>
                    )}
                    {message.isGeneratingAudio && (
                      <div className="flex flex-col items-start space-y-2 mt-2">
                        <div className="flex items-center space-x-2">
                          <div className="glass-morphism flex items-center justify-center w-10 h-10 rounded-full">
                            <div className="h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                          <span className="text-sm text-gray-300">Generating audio...</span>
                        </div>
                      </div>
                    )}
                    {message.additional_kwargs?.audio_data && (
                      <AudioPlayer audioData={message.additional_kwargs.audio_data} />
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="glass-morphism-dark border-t border-emerald-700/20 p-6">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            <div className="flex flex-col space-y-6">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="What are you into?"
                  className="w-full px-6 py-4 bg-emerald-900/20 text-lg text-emerald-100 placeholder-emerald-300/70 rounded-2xl border border-emerald-600/30 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/30 transition-all duration-300 outline-none glass-morphism"
                />
                <div className="absolute inset-0 pointer-events-none rounded-2xl bg-gradient-to-r from-emerald-500/5 to-emerald-300/5"></div>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className={`px-12 py-3 rounded-xl text-lg font-medium transition-all duration-300 ${!input.trim() || isLoading
                      ? "bg-emerald-800/20 text-emerald-300/50 cursor-not-allowed"
                      : "glass-morphism bg-emerald-700/30 text-emerald-300 hover:bg-emerald-600/40 hover:text-emerald-200"
                    }`}
                >
                  Generate Audio Comedy Show
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
